import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { importFaqs } from '@/apis/faq'
import faqService from '@/services/faqService'
import { useApiReady } from '@/hooks/useApiReady'
import type { FaqCatalogImportResult, FaqCatalogImportRow, FaqsListFilters } from '@/types/faq'

export const faqsKeys = {
  all: ['faqs'] as const,
  lists: () => [...faqsKeys.all, 'list'] as const,
  list: (params: FaqsListFilters & { teamId?: string }) =>
    [...faqsKeys.lists(), params] as const,
}

export function useFaqs(
  filters: FaqsListFilters & { team_id?: string },
  options?: { enabled?: boolean }
) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: faqsKeys.list({
      page: filters.page,
      pageSize: filters.pageSize,
      search: filters.search,
      visibilityType: filters.visibility_type,
      topics: filters.topics,
      teamTagIds: filters.team_tag_ids,
      teamUnitIds: filters.team_unit_ids,
      freelancerIds: filters.freelancer_ids,
      teamId: filters.team_id,
    }),
    queryFn: async () => {
      const response = await faqService.getFaqs({
        page: filters.page,
        pageSize: filters.pageSize,
        search: filters.search,
        visibility_type: filters.visibility_type || undefined,
        topics: filters.topics,
        team_tag_ids: filters.team_tag_ids,
        team_unit_ids: filters.team_unit_ids,
        freelancer_ids: filters.freelancer_ids,
        team_id: filters.team_id || undefined,
      })

      if (response.success) {
        return {
          list: response.data.list,
          total: response.data.total,
          pageSize: response.data.pageSize ?? filters.pageSize,
          availableTopics: response.data.availableTopics ?? [],
        }
      }

      return { list: [], total: 0, pageSize: filters.pageSize, availableTopics: [] }
    },
    enabled: apiReady && options?.enabled !== false,
  })
}

export function useImportFaqsCsv() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (rows: FaqCatalogImportRow[]) => {
      const res = await importFaqs(rows)
      if (res.code === 200 && res.data) {
        return res.data as FaqCatalogImportResult
      }
      throw new Error(
        typeof res.message === 'string' && res.message ? res.message : 'FAQ_IMPORT_FAILED'
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: faqsKeys.all })
    },
  })
}
